import { ThunkActionCreator } from "../interfaces/thunk";
import { Dispatch } from "../interfaces/dispatch";
import axios from "axios";
import Config from "../config";
import { getHeaders } from "../helpers/headers";
import { handleActions } from "redux-actions";
import { AnyAction } from "../interfaces/action";
import { FIRST_PAGE, PAGE_SIZE } from "helpers/values";

// AbortController for cancelling previous requests to prevent race conditions
let summaryController: AbortController | null = null;

function getCancelableSummaryAxios() {
  if (summaryController) {
    summaryController.abort();
  }
  summaryController = new AbortController();
  return { signal: summaryController.signal };
}

export const FETCH_FILTER_INFO_REQUEST = "FETCH_FILTER_INFO_REQUEST";
export const FETCH_FILTER_INFO = "FETCH_FILTER_INFO";
export const FETCH_FILTER_INFO_FAILED = "FETCH_FILTER_INFO_FAILED";
export const FETCH_SUMMARY_REQUEST = "FETCH_SUMMARY_REQUEST";
export const FETCH_SUMMARY = "FETCH_SUMMARY";
export const FETCH_SUMMARY_FAILED = "FETCH_SUMMARY_FAILED";
export const UPDATE_PACKAGE_SEGMENTATION_TYPE = "UPDATE_PACKAGE_SEGMENTATION_TYPE";


const fetchPackageSummaryFilters: ThunkActionCreator<Promise<void>> =
  () => (dispatch: Dispatch) => {
    dispatch({ type: FETCH_FILTER_INFO_REQUEST });
    return axios
      .get(`${Config.API_SERVICE_URL}/api/reports/packageSummary/dropdowns`, getHeaders())
      .then((response) => {
        dispatch({ type: FETCH_FILTER_INFO, payload: response.data });
      })
      .catch((_) => {
        dispatch({ type: FETCH_FILTER_INFO_FAILED });
      });
  };
const updateSegmentationType = (segmentationType: string) => ({
  type: UPDATE_PACKAGE_SEGMENTATION_TYPE,
  payload: segmentationType,
});
const fetchPackageSummary: ThunkActionCreator<Promise<void>> =
  (id, segmentationTypes, packageTypes, storeIds, page = 1, size = 10, comparisonId) =>
  (dispatch: Dispatch) => {
    const { signal } = getCancelableSummaryAxios();
    dispatch({ type: FETCH_SUMMARY_REQUEST });

    const params = new URLSearchParams();
    let storeIdsParam: string = "";
    let packageTypesParam = [];
    if (Array.isArray(segmentationTypes) && segmentationTypes.length > 0) {
      params.set("segmentationType", segmentationTypes.join(","));
    }
    if (Array.isArray(packageTypes) && packageTypes.length > 0) {
      packageTypesParam = packageTypes;
    }
    if (Array.isArray(storeIds) && storeIds.length > 0) {
      storeIdsParam = storeIds.join(",");
    }
    params.set("page", String(page));
    params.set("size", String(size));

    return axios
      .post(
        `${Config.API_SERVICE_URL}/api/reports/packageSummary/${id}/${
          comparisonId > -1 ? comparisonId : ""
        }?${params.toString()}`,
        { storeIds: storeIdsParam, packageTypes: packageTypesParam },
        { ...getHeaders(), signal }
      )
      .then((response) => {
        dispatch({ type: FETCH_SUMMARY, payload: response.data });
      })
      .catch((err) => {
        if (err.name === "CanceledError") {
          console.log("Previous package summary request cancelled");
        } else {
        dispatch({ type: FETCH_SUMMARY_FAILED });
        }
      });
  };

const actions = {
  [FETCH_FILTER_INFO_REQUEST]: (state: PackageSummaryState) => ({
    ...state,
    loading: true,
  }),
  [FETCH_FILTER_INFO]: (state: PackageSummaryState, action: AnyAction): PackageSummaryState => ({
    ...state,
    loading: false,
    filters: { ...action.payload },
  }),
  [FETCH_FILTER_INFO_FAILED]: (state: PackageSummaryState) => ({
    ...state,
    loading: false,
    error: true,
    filters: undefined,
  }),
  [FETCH_SUMMARY_REQUEST]: (state: PackageSummaryState) => ({
    ...state,
    loading: true,
    tableLoading: true,
  }),
  [FETCH_SUMMARY]: (state: PackageSummaryState, action: AnyAction): PackageSummaryState => ({
    ...state,
    loading: false,
    content: { ...action.payload, count: action.payload.count || state.content.count },
    tableLoading: false,
  }),
  [FETCH_SUMMARY_FAILED]: (state: PackageSummaryState) => ({
    ...state,
    loading: false,
    error: true,
    content: {
      projectId: "",
      comparisonId: null,
      size: PAGE_SIZE,
      page: FIRST_PAGE,
      segmentationType: "",
      count: 0,
      report: [],
    },
    tableLoading: false,
  }),
  [UPDATE_PACKAGE_SEGMENTATION_TYPE]: (
    state: PackageSummaryState,
    action: AnyAction
  ): PackageSummaryState => ({
    ...state,
    segmentationType: action.payload,
  }),
};
export interface PackageSummaryState {
  content: {
    projectId: string;
    comparisonId: string | null;
    size: number;
    page: number;
    segmentationType: string;
    count: number;
    report: any;
  };
  error: boolean;
  loading: boolean;
  filters: any;
  tableLoading: boolean;
  segmentationType: string;
}

const initialState: PackageSummaryState = {
  loading: false,
  error: false,
  content: {
    projectId: "",
    comparisonId: null,
    size: PAGE_SIZE,
    page: FIRST_PAGE,
    segmentationType: "",
    count: 0,
    report: [],
  },
  filters: {},
  tableLoading: false,
  segmentationType: "subcategory",
};

export { fetchPackageSummaryFilters, fetchPackageSummary, updateSegmentationType };

export default handleActions<PackageSummaryState, AnyAction>(actions, initialState);
